import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import References from './References';

const Methodology: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const sectionHeading = {
    fontSize: '24px',
    fontWeight: 700,
    mb: 2,
    color: theme.palette.text.primary,
  } as const;

  const body = {
    fontSize: '16px',
    lineHeight: 1.7,
    color: theme.palette.text.secondary,
    mb: 2,
  } as const;

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, py: 4 }}>
      {/* Hero */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '36px', sm: '48px' },
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 2,
            color: theme.palette.text.primary,
            letterSpacing: '-0.02em',
          }}
        >
          How emotify works
        </Typography>
        <Typography sx={{ fontSize: '18px', color: theme.palette.text.secondary, fontWeight: 500 }}>
          The science and the method behind your playlist's emotional heatmap
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {/* Two dimensions */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Two dimensions of feeling</Typography>
          <Typography sx={body}>
            Decades of music-psychology research have shown that the emotions we hear in music can be mapped
            surprisingly well onto just two dimensions: <strong>valence</strong> — how positive or negative a
            piece feels, from sad to happy — and <strong>energy</strong> (also called arousal) — how calm or
            intense it is. This is the circumplex model of affect, first proposed by psychologist James Russell
            in 1980 and validated many times since in music research (Russell, 1980; Thayer, 1989).
          </Typography>
          <Typography sx={body}>
            Plot valence on the horizontal axis and energy on the vertical, and every song lands somewhere on a
            two-dimensional plane. Four broad emotional quadrants emerge, which emotify labels
            Turbulent, Energetic, Melancholic, and Peaceful.
          </Typography>
        </Box>

        {/* Diagram */}
        <Box
          sx={{
            my: 2,
            p: 5,
            borderRadius: '20px',
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.06)',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <svg width="600" height="500" viewBox="0 0 600 500" style={{ maxWidth: '100%', height: 'auto', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
            <rect width="600" height="500" fill={isDark ? '#1B1B30' : 'white'} />
            <line x1="300" y1="60" x2="300" y2="420" stroke={isDark ? '#555' : '#ccc'} strokeWidth="2" strokeDasharray="5,5" />
            <line x1="130" y1="240" x2="470" y2="240" stroke={isDark ? '#555' : '#ccc'} strokeWidth="2" strokeDasharray="5,5" />
            <text x="300" y="40" textAnchor="middle" fontSize="15" fontWeight="600" fill={isDark ? '#F8FAFC' : '#000'}>High Energy (intense)</text>
            <text x="300" y="460" textAnchor="middle" fontSize="15" fontWeight="600" fill={isDark ? '#F8FAFC' : '#000'}>Low Energy (calm)</text>
            <text x="80" y="245" textAnchor="middle" fontSize="15" fontWeight="600" fill={isDark ? '#F8FAFC' : '#000'}>Sad</text>
            <text x="520" y="245" textAnchor="middle" fontSize="15" fontWeight="600" fill={isDark ? '#F8FAFC' : '#000'}>Happy</text>
            <text x="200" y="130" textAnchor="middle" fontSize="16" fontWeight="600" fill={isDark ? '#ccc' : '#333'}>Turbulent</text>
            <text x="200" y="150" textAnchor="middle" fontSize="13" fill={isDark ? '#94A3B8' : '#666'}>(Angry, Tense)</text>
            <text x="400" y="130" textAnchor="middle" fontSize="16" fontWeight="600" fill={isDark ? '#ccc' : '#333'}>Energetic</text>
            <text x="400" y="150" textAnchor="middle" fontSize="13" fill={isDark ? '#94A3B8' : '#666'}>(Happy, Excited)</text>
            <text x="200" y="350" textAnchor="middle" fontSize="16" fontWeight="600" fill={isDark ? '#ccc' : '#333'}>Melancholic</text>
            <text x="200" y="370" textAnchor="middle" fontSize="13" fill={isDark ? '#94A3B8' : '#666'}>(Sad, Depressed)</text>
            <text x="400" y="350" textAnchor="middle" fontSize="16" fontWeight="600" fill={isDark ? '#ccc' : '#333'}>Peaceful</text>
            <text x="400" y="370" textAnchor="middle" fontSize="13" fill={isDark ? '#94A3B8' : '#666'}>(Calm, Relaxed)</text>
            <circle cx="300" cy="240" r="5" fill={isDark ? '#F8FAFC' : '#000'} />
            <rect x="130" y="60" width="340" height="360" fill="none" stroke={isDark ? '#444' : '#ddd'} strokeWidth="2" rx="12" />
          </svg>
        </Box>

        {/* From audio to coordinates */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>From audio to coordinates</Typography>
          <Typography sx={body}>
            Research by Eerola and Vuoskoski (2011) demonstrated that these dimensions can be reliably estimated
            from the acoustic features of a recording. Tempo, loudness, and spectral characteristics correlate
            strongly with perceived energy, while harmonic complexity and mode (major vs. minor) shape valence
            (Gabrielsson &amp; Lindström, 2001).
          </Typography>
          <Typography sx={body}>
            For each track in the playlist you paste, emotify looks up its valence and energy values (each on a
            0–1 scale) from audio-feature data, then places the song on the emotional plane above.
          </Typography>
        </Box>

        {/* How the heatmap is built */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>How the heatmap is built</Typography>
          <Typography sx={body}>
            A single dot per song would be noisy and hard to read, so emotify turns the cloud of points into a
            smooth density field — a heatmap. Each track contributes weight to its location on a fine grid using
            bilinear interpolation, so a song sitting between grid cells influences all of its neighbours
            proportionally. The grid is then smoothed with a Gaussian blur, which merges nearby songs into the
            warm, organic blobs you see.
          </Typography>
          <Typography sx={body}>
            The result is a map where <strong>bright, warm regions mean many songs cluster around that mood</strong>,
            and cool or empty areas mean few. It's a portrait of where your playlist spends its emotional time —
            and, often, a surprise: a "chill" playlist can turn out to be quietly energetic, and a workout mix
            can hide real melancholy.
          </Typography>
        </Box>

        {/* Why it matters */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Why it's worth looking at</Typography>
          <Typography sx={body}>
            Music has a real effect on mood, and consciously engaging with its emotional content is linked to
            better mood regulation, stress reduction, and self-awareness (Thoma et al., 2013). Seeing the shape
            of your listening laid out can be genuinely revealing — and it's a fun, shareable way to see yourself
            reflected in the songs you love.
          </Typography>
          <Typography sx={body}>
            A note on interpretation: these labels are an estimate for curiosity and entertainment, derived from
            audio-feature data — not a definitive verdict on any song. Read more about the tool on the{' '}
            <Link to="/about" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>About page</Link>.
          </Typography>
        </Box>

        {/* References */}
        <References />
      </Box>
    </Box>
  );
};

export default Methodology;
